import type {
  TemplateConditionExpression,
  TemplateConditionOperator,
  TemplateField,
} from '@dispatch/schemas';

function editableValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

const operators: readonly { value: TemplateConditionOperator; label: string }[] = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Does not equal' },
  { value: 'is_empty', label: 'Is empty' },
  { value: 'is_not_empty', label: 'Is not empty' },
  { value: 'greater_than', label: 'Greater than' },
  { value: 'less_than', label: 'Less than' },
  { value: 'contains', label: 'Contains' },
];

function defaultCondition(fields: readonly TemplateField[]): TemplateConditionExpression {
  return {
    kind: 'condition',
    fieldId: fields[0]?.id ?? '',
    operator: 'is_not_empty',
  };
}

function ConditionLeaf({
  expression,
  fields,
  onChange,
  onRemove,
  canRemove,
}: {
  expression: Extract<TemplateConditionExpression, { kind: 'condition' }>;
  fields: readonly TemplateField[];
  onChange: (expression: TemplateConditionExpression) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const needsValue = !['is_empty', 'is_not_empty'].includes(expression.operator);
  return (
    <div className="condition-row">
      <select
        aria-label="Condition field"
        value={expression.fieldId}
        onChange={(event) => onChange({ ...expression, fieldId: event.target.value })}
      >
        {fields.map((field) => (
          <option key={field.id} value={field.id}>
            {field.label}
          </option>
        ))}
      </select>
      <select
        aria-label="Condition operator"
        value={expression.operator}
        onChange={(event) =>
          onChange({
            ...expression,
            operator: event.target.value as TemplateConditionOperator,
            ...(['is_empty', 'is_not_empty'].includes(event.target.value)
              ? { value: undefined }
              : {}),
          })
        }
      >
        {operators.map((operator) => (
          <option key={operator.value} value={operator.value}>
            {operator.label}
          </option>
        ))}
      </select>
      {needsValue ? (
        <input
          aria-label="Condition value"
          value={editableValue(expression.value)}
          onChange={(event) => onChange({ ...expression, value: event.target.value })}
        />
      ) : (
        <span className="condition-no-value">No value</span>
      )}
      <button type="button" className="plain" onClick={onRemove} disabled={!canRemove}>
        Remove
      </button>
    </div>
  );
}

export function ConditionEditor({
  expression,
  fields,
  onChange,
  depth = 0,
}: {
  expression: TemplateConditionExpression;
  fields: readonly TemplateField[];
  onChange: (expression: TemplateConditionExpression) => void;
  depth?: number;
}) {
  if (expression.kind === 'condition') {
    return (
      <ConditionLeaf
        expression={expression}
        fields={fields}
        onChange={onChange}
        onRemove={() => undefined}
        canRemove={false}
      />
    );
  }

  const replaceChild = (index: number, next: TemplateConditionExpression) => {
    onChange({
      ...expression,
      conditions: expression.conditions.map((condition, candidateIndex) =>
        candidateIndex === index ? next : condition,
      ),
    });
  };

  const removeChild = (index: number) => {
    if (expression.conditions.length <= 1) return;
    onChange({
      ...expression,
      conditions: expression.conditions.filter((_, candidateIndex) => candidateIndex !== index),
    });
  };

  return (
    <fieldset className="condition-group">
      <legend>{depth === 0 ? 'When' : 'Condition group'}</legend>
      <div className="condition-group-toolbar">
        <label>
          Match
          <select
            value={expression.mode}
            onChange={(event) =>
              onChange({ ...expression, mode: event.target.value === 'any' ? 'any' : 'all' })
            }
          >
            <option value="all">all conditions (AND)</option>
            <option value="any">any condition (OR)</option>
          </select>
        </label>
      </div>

      <div className="condition-group-children">
        {expression.conditions.map((condition, index) =>
          condition.kind === 'condition' ? (
            <ConditionLeaf
              key={`${condition.fieldId}-${index}`}
              expression={condition}
              fields={fields}
              onChange={(next) => replaceChild(index, next)}
              onRemove={() => removeChild(index)}
              canRemove={expression.conditions.length > 1}
            />
          ) : (
            <div key={`group-${index}`} className="condition-nested-group">
              <ConditionEditor
                expression={condition}
                fields={fields}
                onChange={(next) => replaceChild(index, next)}
                depth={depth + 1}
              />
              <button
                type="button"
                className="plain"
                onClick={() => removeChild(index)}
                disabled={expression.conditions.length <= 1}
              >
                Remove group
              </button>
            </div>
          ),
        )}
      </div>

      <div className="actions condition-group-actions">
        <button
          type="button"
          className="secondary"
          onClick={() =>
            onChange({
              ...expression,
              conditions: [...expression.conditions, defaultCondition(fields)],
            })
          }
          disabled={fields.length === 0}
        >
          Add condition
        </button>
        <button
          type="button"
          className="secondary"
          onClick={() =>
            onChange({
              ...expression,
              conditions: [
                ...expression.conditions,
                {
                  kind: 'group',
                  mode: 'all',
                  conditions: [defaultCondition(fields)],
                },
              ],
            })
          }
          disabled={fields.length === 0 || depth >= 4}
        >
          Add group
        </button>
      </div>
    </fieldset>
  );
}
